/**
 * Hooks do runtime do microsite e auth surfaces.
 *
 * Mantém a API pública dos hooks intacta para o LiveSocket,
 * mas remove estes blocos do entrypoint principal.
 */

export const MicrositeCalendarGestures = {
  mounted() {
    this._touchStart = null;
    this._touchId = null;
    this._moved = false;
    this._maxDuration = 500;
    this._minDistance = 100;
    this._hDominance = 1.4;

    const isInteractive = (el) => {
      const tag = (el?.tagName || '').toLowerCase();
      return ['input', 'select', 'textarea', 'button'].includes(tag) || el?.isContentEditable;
    };

    this._onStart = (e) => {
      try {
        const t = (e.changedTouches && e.changedTouches[0]) || e.touches?.[0];
        if (!t) return;
        if (isInteractive(e.target)) return;
        this._touchId = t.identifier;
        this._touchStart = { x: t.clientX, y: t.clientY, at: performance.now() };
        this._moved = false;
      } catch (_) { }
    };

    this._onMove = (e) => {
      if (e.touches && e.touches.length) this._moved = true;
    };

    this._onEnd = (e) => {
      try {
        const tlist = e.changedTouches || [];
        let t = null;

        for (let i = 0; i < tlist.length; i++) {
          if (this._touchId == null || tlist[i].identifier === this._touchId) {
            t = tlist[i];
            break;
          }
        }

        if (!t || !this._touchStart) return;

        const dx = t.clientX - this._touchStart.x;
        const dy = t.clientY - this._touchStart.y;
        const dt = performance.now() - this._touchStart.at;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);

        if (dt < this._maxDuration && adx > this._minDistance && adx > ady * this._hDominance) {
          const dir = dx < 0 ? 'left' : 'right';

          try {
            if (dir === 'left') {
              this.pushEvent('next_week', {});
            } else {
              this.pushEvent('previous_week', {});
            }
          } catch (_) { }
        }
      } finally {
        this._touchStart = null;
        this._touchId = null;
        this._moved = false;
      }
    };

    try {
      const focusId = this.el?.dataset?.focusId;
      if (focusId) {
        const el = document.getElementById(focusId);
        if (el && typeof el.scrollIntoView === 'function') {
          setTimeout(() => {
            try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { }
          }, 50);
        }
      }

      this.handleEvent && this.handleEvent('focus_cell', ({ id }) => {
        try {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (_) { }
      });
    } catch (_) { }

    this.el.addEventListener('touchstart', this._onStart, { passive: true });
    this.el.addEventListener('touchmove', this._onMove, { passive: true });
    this.el.addEventListener('touchend', this._onEnd, { passive: true });
  },

  destroyed() {
    try {
      if (this._onStart) this.el.removeEventListener('touchstart', this._onStart, { passive: true });
      if (this._onMove) this.el.removeEventListener('touchmove', this._onMove, { passive: true });
      if (this._onEnd) this.el.removeEventListener('touchend', this._onEnd, { passive: true });
    } catch (_) { }

    this._touchStart = null;
    this._touchId = null;
    this._moved = false;
  }
};

export const EdgeSwipeBack = {
  mounted() {
    this._start = null;
    this._id = null;
    this._edge = 24;
    this._minDx = 120;
    this._maxDy = 60;
    this._maxDt = 600;

    const isMobile = () => (window.innerWidth || 0) < 1024;
    const isInteractive = (el) => {
      const tag = (el?.tagName || '').toLowerCase();
      return ['input', 'select', 'textarea', 'button', 'a'].includes(tag) || el?.isContentEditable;
    };
    const withinCalendar = (el) => {
      try {
        return el.closest && !!el.closest('#microsite-calendar-gesture-box');
      } catch (_) {
        return false;
      }
    };

    this._onStart = (e) => {
      if (!isMobile()) return;
      const t = (e.changedTouches && e.changedTouches[0]) || e.touches?.[0];
      if (!t) return;
      if (t.clientX > this._edge) return;
      if (isInteractive(e.target)) return;
      if (withinCalendar(e.target)) return;

      this._id = t.identifier;
      this._start = { x: t.clientX, y: t.clientY, at: performance.now() };
    };

    this._onEnd = (e) => {
      if (!this._start) return;
      const tlist = e.changedTouches || [];
      let t = null;

      for (let i = 0; i < tlist.length; i++) {
        if (this._id == null || tlist[i].identifier === this._id) {
          t = tlist[i];
          break;
        }
      }

      if (!t) return;

      const dx = t.clientX - this._start.x;
      const dy = t.clientY - this._start.y;
      const dt = performance.now() - this._start.at;

      if (dt <= this._maxDt && dx > this._minDx && Math.abs(dy) < this._maxDy) {
        const view = this.el?.dataset?.viewMode;
        if (view && view !== 'home') {
          try { this.pushEvent('go_home', {}); } catch (_) { }
        }
      }

      this._start = null;
      this._id = null;
    };

    this.el.addEventListener('touchstart', this._onStart, { passive: true });
    this.el.addEventListener('touchend', this._onEnd, { passive: true });
  },

  destroyed() {
    try {
      if (this._onStart) this.el.removeEventListener('touchstart', this._onStart, { passive: true });
      if (this._onEnd) this.el.removeEventListener('touchend', this._onEnd, { passive: true });
    } catch (_) { }

    this._start = null;
    this._id = null;
  }
};

const micrositeQrState = (el) => {
  const path = el?.dataset?.path || '/microsites/reserves';
  const defaultHost = el?.dataset?.host || (window.location.protocol + '//' + window.location.hostname);
  const host = defaultHost.replace(/\/$/, '');
  const size = el?.dataset?.size || '280x280';
  const absOverride = el?.dataset?.abs;
  const dataUri = el?.dataset?.qr;
  const abs = absOverride || `${host}${path}`;
  const remoteUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${encodeURIComponent(size)}&data=${encodeURIComponent(abs)}`;

  return {
    abs,
    resolvedSrc: dataUri || remoteUrl
  };
};

const applyMicrositeQrCardHeight = (el) => {
  try {
    if (!el || !el.isConnected) return;

    if (window.innerHeight > window.innerWidth) {
      el.style.minHeight = '';
      delete el.dataset.baseHeight;
      return;
    }

    let base = parseInt(el.dataset.baseHeight || '0', 10);
    if (!base || Number.isNaN(base)) {
      const previousMinHeight = el.style.minHeight;
      if (previousMinHeight) el.style.minHeight = '';
      base = Math.max(el.scrollHeight, el.getBoundingClientRect().height | 0);
      if (previousMinHeight) el.style.minHeight = previousMinHeight;
      el.dataset.baseHeight = String(base);
    }

    if (base > 0) {
      const target = Math.round(base * 1.2);
      const current = parseInt((el.style.minHeight || '0').replace('px', ''), 10) || 0;
      if (Math.abs(current - target) > 2) {
        el.style.minHeight = `${target}px`;
      }
    }
  } catch (_) { }
};

export const MicrositeQR = {
  mounted() {
    try {
      const img = this.el.querySelector('img[data-role="qr-img"]');
      if (!img) return;

      const { abs, resolvedSrc } = micrositeQrState(this.el);
      if (img.src !== resolvedSrc) {
        img.src = resolvedSrc;
      }
      img.title = abs;

      img.addEventListener('load', () => requestAnimationFrame(() => applyMicrositeQrCardHeight(this.el)), {
        once: true
      });
      requestAnimationFrame(() => applyMicrositeQrCardHeight(this.el));
    } catch (e) {
      console.warn('MicrositeQR hook failed:', e);
    }
  },

  updated() {
    try {
      const img = this.el.querySelector('img[data-role="qr-img"]');
      if (!img) return;

      const { abs, resolvedSrc } = micrositeQrState(this.el);
      if (img.src !== resolvedSrc) img.src = resolvedSrc;
      if (img.title !== abs) img.title = abs;

      applyMicrositeQrCardHeight(this.el);
    } catch (_) { }
  }
};

const syncAuthLayout = (el) => {
  try {
    if (!el || !el.isConnected) return;

    const isPortrait = window.innerHeight > window.innerWidth;
    document.body.dataset.kioskLayout = isPortrait ? 'portrait' : 'landscape';

    const leftCol = document.getElementById('auth-left-col');
    const right = document.getElementById('auth-right-keypad-card');
    if (!leftCol || !right) return;

    if (isPortrait) {
      // Portrait sizing belongs to the grid; discard landscape measurements
      // even when the QR image loaded before the screen was rotated.
      for (const card of leftCol.querySelectorAll('#auth-left-rfid-card, #microsite-qr-card')) {
        card.style.minHeight = '';
        delete card.dataset.baseHeight;
      }
      right.style.overflow = '';
      return;
    }

    const leftRect = leftCol.getBoundingClientRect();
    const gridRect = el.getBoundingClientRect();
    let desired = Math.floor(leftRect.height);

    const footer = document.getElementById('kiosk-footer') || document.querySelector('footer');
    let maxAvail = 0;
    if (footer) {
      const footerRect = footer.getBoundingClientRect();
      maxAvail = Math.floor(footerRect.top - gridRect.top - 8);
    } else {
      maxAvail = Math.floor(window.innerHeight - gridRect.top - 8);
    }

    if (maxAvail > 0) desired = Math.min(desired, maxAvail);
    if (desired <= 0) return;

    const needsScroll = right.scrollHeight > desired + 1;
    right.style.overflow = needsScroll ? 'auto' : 'hidden';
  } catch (e) {
    console.warn('AuthLayoutSync failed:', e);
  }
};

export const AuthLayoutSync = {
  mounted() {
    this._sync = this.sync.bind(this);
    document.body.dataset.kioskLayout =
      window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';

    requestAnimationFrame(this._sync);

    this._onResize = () => requestAnimationFrame(this._sync);
    window.addEventListener('resize', this._onResize, { passive: true });

    try {
      this._ro = new ResizeObserver(() => this._sync());
      const leftCol = document.getElementById('auth-left-col');
      const rfid = document.getElementById('auth-left-rfid-card');
      const qr = document.getElementById('microsite-qr-card');
      const right = document.getElementById('auth-right-keypad-card');
      [leftCol, rfid, qr, right].forEach((node) => node && this._ro.observe(node));

      const qrImg = qr?.querySelector('img[data-role="qr-img"]');
      if (qrImg) qrImg.addEventListener('load', this._sync, { once: true });
    } catch (_) { }
  },

  updated() {
    this._sync();
  },

  destroyed() {
    try { window.removeEventListener('resize', this._onResize, { passive: true }); } catch (_) { }
    try { this._ro && this._ro.disconnect && this._ro.disconnect(); } catch (_) { }

    const right = document.getElementById('auth-right-keypad-card');
    if (right) {
      right.style.height = '';
      right.style.maxHeight = '';
      right.style.overflow = '';
    }
  },

  sync() {
    syncAuthLayout(this.el);
  }
};

export const MicrositeHooks = {
  MicrositeCalendarGestures,
  EdgeSwipeBack,
  MicrositeQR,
  AuthLayoutSync
};
